/**
 * useIOSNowPlaying.ts — iOS lock screen integration for Maestro.
 *
 * Architecture:
 *   useIOSNowPlaying        — push metadata to MPNowPlayingInfoCenter
 *   useIOSPlaybackSync      — push play/pause/seek state (iOS interpolates progress natively)
 *   useIOSRemoteCommands    — lock screen buttons → TransportCommand / QueueController
 *   useIOSBackgroundAdvance — native Timer fires when track ends (legacy fallback)
 *   useIOSForegroundSync    — reconcile Zustand with native's authoritative state on wake
 *   useNativeQueueSync      — push queue/auth to native so it can advance WITHOUT WebView
 *
 * Key design principles:
 *   1. WebAudioRenderer is NEVER touched — it handles local browser audio only
 *   2. For remote renderers, foreground advance comes from the renderer via BATON (not from us)
 *   3. NativeQueueManager (Swift) is the single source of truth for background playback.
 *      It fetches stream URLs via URLSession and calls MaestroCore.playUri() directly.
 *      The WebView is NOT in the critical path for background track advancement.
 *   4. A generation counter prevents double-advance if both native and BATON fire
 *   5. iOS natively interpolates the lock screen progress bar from (elapsed + playbackRate)
 *      — we send state ONCE per play/pause/seek, NOT on a polling interval
 *   6. Queue sync is immediate on every user interaction (skip, shuffle, reorder).
 *      Auth tokens are synced on mount and every 30s to survive long background sessions.
 */
import { useEffect, useRef } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { isIOSNativeApp } from '../utils/platform';
import { RADIO_ARTWORK_URL } from '../handlers/RadioHandler';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { TransportCommand } from '../transport/TransportCommand';

// ─── Artwork URL resolver ────────────────────────────────────────────────
// Track objects use different field names depending on source (Qobuz, local, radio).
// This tries every known path and normalizes the URL for iOS (HTTPS, no protocol-relative).
function resolveArtworkUrl(track: any): string {
  const raw = track.artworkUrlHiRes
    || track.artworkUrl
    || track.image?.large
    || track.coverUrl
    || track.image?.small
    || track.image?.thumbnail
    || track.albumArt
    || track.thumbnail
    || track.artwork
    || '';
  if (!raw) {
    if (track.source === 'radio' || track.id === 'octave_radio') return RADIO_ARTWORK_URL;
    return '';
  }
  let url = String(raw).trim();
  if (url.startsWith('//')) url = 'https:' + url;
  if (url.startsWith('http://')) url = url.replace('http://', 'https://');

  // Prefer 600px artwork for crisp lock screen display
  if (url.includes('static.qobuz.com') && /_\d+\.jpg/.test(url)) {
    url = url.replace(/_\d+\.jpg/, '_600.jpg');
  }
  return url;
}

// ─── Generation counter ──────────────────────────────────────────────────
// Incremented on every track change. Both the native Timer and renderer events
// can trigger advance — the counter ensures only the first one wins.
let advanceGeneration = 0;

// ─── Native bridge helper ────────────────────────────────────────────────
function postToNative(type: string, payload: Record<string, unknown>) {
  try {
    const native = (window as any).__maestroNative;
    if (native?.postMessage) {
      native.postMessage(type, payload);
      return;
    }
    if ((window as any).webkit?.messageHandlers?.maestroNative?.postMessage) {
      (window as any).webkit.messageHandlers.maestroNative.postMessage({
        action: type,
        data: payload,
      });
    }
  } catch (e) {
    console.warn('[iOS] postToNative error:', e);
  }
}

// ─── 1. useIOSNowPlaying ────────────────────────────────────────────────
// Pushes track metadata to the lock screen on track changes.
export function useIOSNowPlaying(): void {
  const currentTrack = useMaestroStore((s) => s.currentTrack);
  const lastTrackIdRef = useRef<string | null>(null);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear lock screen when no track for 3 seconds (handles brief null during _advance())
  useEffect(() => {
    if (!isIOSNativeApp()) return;

    if (!currentTrack) {
      clearTimerRef.current = setTimeout(() => {
        console.log('[iOS:NowPlaying] No track for 3s — clearing');
        postToNative('clearNowPlaying', {});
        lastTrackIdRef.current = null;
      }, 3000);
    } else if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }

    return () => {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
    };
  }, [currentTrack]);

  // Push metadata on track change + increment generation counter
  useEffect(() => {
    if (!isIOSNativeApp() || !currentTrack) return;

    const trackId = currentTrack.id || `${currentTrack.title}-${currentTrack.artist}`;
    if (trackId === lastTrackIdRef.current) return;
    lastTrackIdRef.current = trackId;

    advanceGeneration++;

    const artworkURL = resolveArtworkUrl(currentTrack);
    const durationRaw = currentTrack.duration || 0;
    // Qobuz/store durations are already in seconds. Only convert if clearly ms (> 10000 = 2.7+ hours)
    const durationSecs = durationRaw > 10000 ? durationRaw / 1000 : durationRaw;

    console.log('[iOS:NowPlaying] Track changed → generation', advanceGeneration, currentTrack.title);
    console.log('[iOS:NowPlaying] Artwork debug:', {
      artworkUrl: currentTrack.artworkUrl,
      artworkUrlHiRes: currentTrack.artworkUrlHiRes,
      coverUrl: (currentTrack as any).coverUrl,
      image: (currentTrack as any).image,
      albumArt: (currentTrack as any).albumArt,
      artwork: (currentTrack as any).artwork,
      thumbnail: (currentTrack as any).thumbnail,
      resolved: artworkURL,
      allKeys: Object.keys(currentTrack),
    });

    const activeRenderer = useMaestroStore.getState().activeConnectorRenderer;

    postToNative('updateNowPlaying', {
      title: currentTrack.title || 'Unknown Track',
      artist: currentTrack.artist || 'Unknown Artist',
      album: currentTrack.album || '',
      artworkURL,
      duration: durationSecs,
      elapsed: 0,
      playbackRate: 1.0,
      isLocal: !activeRenderer,
    });

    // Send artwork as base64 via <img> + canvas. If CORS blocks canvas read,
    // Swift URLSession handles it natively (the artworkURL was already sent above).
    // We also retry after a delay to handle race conditions where the Swift
    // URLSession might have failed on first attempt.
    if (artworkURL) {
      const tryCanvasBase64 = (imgEl: HTMLImageElement): boolean => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = imgEl.naturalWidth;
          canvas.height = imgEl.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(imgEl, 0, 0);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
            const base64 = dataUrl.split(',')[1];
            if (base64 && base64.length > 100) {
              postToNative('setArtwork', { base64 });
              console.log('[iOS:NowPlaying] Sent artwork base64 via canvas, length:', base64.length);
              return true;
            }
          }
        } catch {
          // Canvas tainted by CORS — expected for most CDN images
        }
        return false;
      };

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (!tryCanvasBase64(img)) {
          console.log('[iOS:NowPlaying] Canvas base64 failed (CORS), Swift URLSession will handle artwork');
        }
      };
      img.onerror = () => {
        // crossOrigin='anonymous' can cause some servers to reject the request.
        // Retry without it — the image loads but canvas will be tainted (expected).
        // Swift URLSession (triggered by artworkURL above) is the primary path anyway.
        console.log('[iOS:NowPlaying] CORS img failed, retrying without crossOrigin');
        const img2 = new Image();
        img2.onload = () => tryCanvasBase64(img2);
        img2.src = artworkURL;
      };
      img.src = artworkURL;

      // Safety net: if artwork still missing after 4s, force a reload on the Swift side.
      // This handles the case where the initial URLSession fetch completed before
      // nowPlayingInfo was fully populated.
      setTimeout(() => {
        postToNative('retryArtwork', { artworkURL });
      }, 4000);
    }
  }, [currentTrack]);
}

// ─── 2. useIOSPlaybackSync ──────────────────────────────────────────────
// Sends elapsed + playbackRate to iOS on play/pause/track change.
// iOS natively interpolates the progress bar from (elapsed + rate) —
// we send ONCE per state transition, NOT on a polling interval.
// Seek position updates reach native via TransportCommand.seek() directly.
export function useIOSPlaybackSync(): void {
  const currentTrack = useMaestroStore((s) => s.currentTrack);
  const playbackState = useMaestroStore((s) => s.playbackState);

  useEffect(() => {
    if (!isIOSNativeApp() || !currentTrack) return;

    // When this device is observing a remote session, ClientSyncHandler pushes
    // lock screen state directly via _pushIOSNowPlaying. Don't overwrite it here.
    if (getSyncHandler().isObserving()) return;

    // Transient states (loading/buffering) must NOT send rate 0 — that would
    // cancel the native track-advance timer even though playback is about to resume.
    // Only send rate 0 for genuine pause/stop.
    if (playbackState === 'loading' || playbackState === 'buffering') {
      console.log('[iOS:PlaybackSync] Skipping rate update during', playbackState);
      return;
    }

    const rate = playbackState === 'playing' ? 1.0 : 0.0;
    const freshPosition = useMaestroStore.getState().position ?? 0;
    const activeRenderer = useMaestroStore.getState().activeConnectorRenderer;

    console.log('[iOS:PlaybackSync] state:', playbackState, 'elapsed:', freshPosition, 'rate:', rate);

    postToNative('updatePlaybackState', {
      playbackRate: rate,
      elapsed: freshPosition,
      isLocal: !activeRenderer,
    });
  }, [playbackState, currentTrack]);
}

// ─── 3. useIOSBackgroundAdvance ─────────────────────────────────────────
// Registers the handler that the native Timer calls when a track expires
// while the iPad is locked / app is backgrounded.
//
// The native Timer in NowPlayingManager.swift fires after (duration - elapsed + 2s).
// It calls evaluateJavaScript("window.__maestroTrackEnded()").
// This handler checks the generation counter to prevent double-advance.
export function useIOSBackgroundAdvance(): void {
  useEffect(() => {
    if (!isIOSNativeApp()) return;

    (window as any).__maestroTrackEnded = async () => {
      const capturedGeneration = advanceGeneration;
      const store = useMaestroStore.getState();

      console.log('[iOS:BackgroundAdvance] Native timer fired. generation:', capturedGeneration,
        'visible:', document.visibilityState, 'state:', store.playbackState);

      if (!store.currentTrack || store.playbackState !== 'playing') {
        console.log('[iOS:BackgroundAdvance] Skipping — not playing');
        return;
      }

      if (capturedGeneration !== advanceGeneration) {
        console.log('[iOS:BackgroundAdvance] Skipping — generation stale',
          capturedGeneration, '!==', advanceGeneration);
        return;
      }

      // If app is visible, the renderer's BATON event should handle advance.
      // Give it a short grace period before taking over.
      if (document.visibilityState === 'visible') {
        console.log('[iOS:BackgroundAdvance] App visible — waiting 2s for renderer event');
        await new Promise(r => setTimeout(r, 2000));

        if (capturedGeneration !== advanceGeneration) {
          console.log('[iOS:BackgroundAdvance] Renderer advanced during grace period — skipping');
          return;
        }
        console.log('[iOS:BackgroundAdvance] Renderer did not advance — taking over');
      }

      console.log('[iOS:BackgroundAdvance] Advancing to next track');
      try {
        const { QueueController } = await import('../transport/QueueController');

        // CRITICAL: await next() so the full chain completes:
        //   setCurrentTrack → getStreamUrl (network) → TransportCommand.playUri (native bridge)
        // The native side holds a UIBackgroundTask to keep the app alive during this.
        // Without await, iOS suspends the WebView before the stream URL resolves.
        await QueueController.next();

        console.log('[iOS:BackgroundAdvance] QueueController.next() completed');

        // Push the new track's metadata to native so the lock screen updates
        // and the next advance timer gets scheduled. The store is already updated
        // because we awaited next() above.
        const newStore = useMaestroStore.getState();
        if (newStore.currentTrack) {
          const artworkURL = resolveArtworkUrl(newStore.currentTrack);
          const durationRaw = newStore.currentTrack.duration || 0;
          const durationSecs = durationRaw > 10000 ? durationRaw / 1000 : durationRaw;
          console.log('[iOS:BackgroundAdvance] Pushing next track to native:',
            newStore.currentTrack.title, 'duration:', durationSecs);
          postToNative('updateNowPlaying', {
            title: newStore.currentTrack.title || 'Unknown Track',
            artist: newStore.currentTrack.artist || 'Unknown Artist',
            album: newStore.currentTrack.album || '',
            artworkURL,
            duration: durationSecs,
            elapsed: 0,
            playbackRate: 1.0,
            isLocal: !newStore.activeConnectorRenderer,
          });
        }
      } catch (e) {
        console.error('[iOS:BackgroundAdvance] Advance failed:', e);
      }
    };

    return () => {
      delete (window as any).__maestroTrackEnded;
    };
  }, []);
}

// ─── 4. useIOSRemoteCommands ────────────────────────────────────────────
// Lock screen button taps → TransportCommand / QueueController.
// Called from Swift via evaluateJavaScript("window.__maestroRemoteCommand('play')").
export function useIOSRemoteCommands(): void {
  useEffect(() => {
    if (!isIOSNativeApp()) return;

    (window as any).__maestroRemoteCommand = async (command: string, payload?: number) => {
      console.log('[iOS:RemoteCommand] Received:', command, payload);

      const { TransportCommand, authorizeIosPause } = await import('../transport/TransportCommand');
      const { QueueController } = await import('../transport/QueueController');

      const store = useMaestroStore.getState();

      switch (command) {
        case 'play':
          TransportCommand.play();
          break;
        case 'pause':
          authorizeIosPause(); TransportCommand.pause(true);
          break;
        case 'togglePlayPause':
          if (store.playbackState === 'playing') {
            authorizeIosPause(); TransportCommand.pause(true);
          } else {
            TransportCommand.play();
          }
          break;
        case 'nextTrack':
          await QueueController.next();
          break;
        case 'previousTrack':
          await QueueController.previous();
          break;
        case 'seek':
          if (payload !== undefined && Number.isFinite(payload)) {
            TransportCommand.seek(payload);
          }
          break;
      }
    };

    return () => {
      delete (window as any).__maestroRemoteCommand;
    };
  }, []);
}

// ─── 5. useIOSForegroundSync ────────────────────────────────────────────
// When the iPad wakes from standby, native may have advanced the queue.
// The __nativeDidAdvance handler reconciles Zustand with native's source of truth.
// We also re-send metadata to the lock screen to keep it fresh.
export function useIOSForegroundSync(): void {
  useEffect(() => {
    if (!isIOSNativeApp()) return;

    // Native calls this when it advances tracks in the background, or on
    // willEnterForeground to push its authoritative queue index.
    (window as any).__nativeDidAdvance = (newIndex: number) => {
      console.log('[iOS:ForegroundSync] Native advanced to index:', newIndex);
      const s = useMaestroStore.getState();
      if (!s.queue?.length) return;

      const effectiveIndex = Math.min(Math.max(0, newIndex), s.queue.length - 1);
      if (effectiveIndex < s.queueIndex) {
        console.warn(
          '[ForegroundSync] Ignoring backwards index:',
          effectiveIndex,
          '< current:',
          s.queueIndex,
        );
        return;
      }
      if (effectiveIndex === s.queueIndex) return;

      const nextTrack = s.queue[effectiveIndex]?.track;
      if (!nextTrack) return;

      console.log('[iOS:ForegroundSync] Reconciling Zustand → index', effectiveIndex, nextTrack.title);
      advanceGeneration++;
      const store = useMaestroStore.getState();
      store.setQueue(s.queue, effectiveIndex);
      store.setCurrentTrack(nextTrack);
      const dur = nextTrack.duration || 0;
      store.updateTime(0, dur > 10000 ? dur / 1000 : dur);
      store.setPlaybackState('playing');
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      console.log('[iOS:ForegroundSync] App became visible — syncing UI');
      const s = useMaestroStore.getState();

      if (s.currentTrack) {
        useMaestroStore.setState({ ...s });

        const artworkURL = resolveArtworkUrl(s.currentTrack);
        const durationRaw = s.currentTrack.duration || 0;
        const durationSecs = durationRaw > 10000 ? durationRaw / 1000 : durationRaw;
        const freshPosition = s.position ?? 0;

        postToNative('updateNowPlaying', {
          title: s.currentTrack.title || 'Unknown Track',
          artist: s.currentTrack.artist || 'Unknown Artist',
          album: s.currentTrack.album || '',
          artworkURL,
          duration: durationSecs,
          elapsed: freshPosition,
          playbackRate: s.playbackState === 'playing' ? 1.0 : 0.0,
          isLocal: !s.activeConnectorRenderer,
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      delete (window as any).__nativeDidAdvance;
    };
  }, []);
}

// ─── 6. useNativeQueueSync ──────────────────────────────────────────────
// Syncs the queue, repeat mode, renderer, and auth tokens to native Swift
// so NativeQueueManager can advance tracks WITHOUT the WebView.
//
// Sync triggers:
//   - Queue changes (new album, playlist loaded, track added/removed)
//   - Queue index changes (skip, previous, shuffle reorder)
//   - Repeat mode changes
//   - Active renderer changes
//   - Auth token changes (login, proactive refresh)
//
// All syncs are immediate (no debounce for user interactions like skip).
export function useNativeQueueSync(): void {
  const queue = useMaestroStore((s) => s.queue);
  const queueIndex = useMaestroStore((s) => s.queueIndex);
  const repeatMode = useMaestroStore((s) => s.repeatMode);
  const activeRenderer = useMaestroStore((s) => s.activeConnectorRenderer);

  // Sync queue state to native on every relevant change
  useEffect(() => {
    if (!isIOSNativeApp()) return;
    if (!queue?.length) return;

    const formatId = (() => {
      if (!activeRenderer) return 27;
      const n = (activeRenderer.name || '').toLowerCase();
      return (n.includes('sonos') || n.includes('rincon')) ? 6 : 27;
    })();

    const nativeTracks = queue.map((qt) => {
      const t = qt.track;
      const qobuzTrackId = t.qobuzTrackId || t.serviceTrackId || t.id || '';
      const durationRaw = t.duration || 0;
      const duration = durationRaw > 10000 ? durationRaw / 1000 : durationRaw;
      return {
        qobuzTrackId,
        trackId: t.id || qobuzTrackId,
        title: t.title || 'Unknown Track',
        artist: t.artist || 'Unknown Artist',
        album: t.album || '',
        duration,
        artworkURL: resolveArtworkUrl(t),
        formatId,
      };
    });

    console.log('[iOS:QueueSync] Syncing', nativeTracks.length, 'tracks, index:', queueIndex,
      'repeat:', repeatMode, 'renderer:', activeRenderer?.id || 'none');

    postToNative('syncQueue', {
      tracks: nativeTracks,
      currentIndex: queueIndex,
      repeatMode,
      rendererId: activeRenderer?.id || null,
      formatId,
    });
  }, [queue, queueIndex, repeatMode, activeRenderer]);

  // Sync auth tokens to native on mount and whenever they change
  const authSyncedRef = useRef(false);
  useEffect(() => {
    if (!isIOSNativeApp()) return;

    const syncAuth = () => {
      try {
        const stored = localStorage.getItem('maestro_auth');
        if (!stored) return;
        const { accessToken, refreshToken } = JSON.parse(stored);
        if (accessToken) {
          postToNative('syncAuthToken', { accessToken, refreshToken: refreshToken || '' });
          if (!authSyncedRef.current) {
            console.log('[iOS:QueueSync] Auth token synced to native');
            authSyncedRef.current = true;
          }
        }
      } catch { /* ignore parse errors */ }
    };

    // Sync immediately on mount
    syncAuth();

    // Re-sync whenever localStorage changes (covers proactive token refresh)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'maestro_auth') syncAuth();
    };
    window.addEventListener('storage', handleStorage);

    // Also poll every 30s as a safety net — StorageEvent doesn't fire for
    // same-tab writes, and the AuthProvider refreshes tokens in the same tab.
    const interval = setInterval(syncAuth, 30_000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);
}
